// Browser side of passkey (Face ID / Touch ID / Windows Hello) sign-in. The server sends WebAuthn
// options as JSON with base64url byte fields; the browser API wants ArrayBuffers, and back again.

const toBytes = (b64url) => {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((b64url.length + 3) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
};

const toB64url = (buf) => {
  let s = "";
  new Uint8Array(buf).forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const descriptors = (list = []) => list.map((c) => ({ ...c, id: toBytes(c.id) }));

export const passkeysSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials?.create;

// Shown on buttons: "Face ID" on Apple phones/tablets, "Touch ID" on Macs, "Windows Hello" on Windows.
export const passkeyLabel = () => {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "Face ID";
  if (/Macintosh/.test(ua)) return navigator.maxTouchPoints > 1 ? "Face ID" : "Touch ID";
  if (/Windows/.test(ua)) return "Windows Hello";
  return "passkey";
};

// A short default name for a new passkey ("iPhone", "Mac", ...), editable later by removing and re-adding.
export const deviceName = () => {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android phone";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
};

export const createPasskey = async (options) => {
  const cred = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: toBytes(options.challenge),
      user: { ...options.user, id: toBytes(options.user.id) },
      excludeCredentials: descriptors(options.excludeCredentials),
    },
  });
  return {
    id: cred.id,
    rawId: toB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64url(cred.response.clientDataJSON),
      attestationObject: toB64url(cred.response.attestationObject),
      transports: cred.response.getTransports?.() || [],
    },
  };
};

export const getPasskey = async (options) => {
  const cred = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: toBytes(options.challenge),
      allowCredentials: descriptors(options.allowCredentials),
    },
  });
  return {
    id: cred.id,
    rawId: toB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64url(cred.response.clientDataJSON),
      authenticatorData: toB64url(cred.response.authenticatorData),
      signature: toB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? toB64url(cred.response.userHandle) : null,
    },
  };
};

// The person closed the Face ID / passkey sheet, or the browser timed it out: not worth an error toast.
export const isCancel = (err) => err?.name === "NotAllowedError" || err?.name === "AbortError";
